import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Download, Ticket as TicketIcon, CalendarPlus } from "lucide-react";
import { api, API, imageUrl, money, fullDate, LOGO_URL } from "@/lib/api";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [nf, setNf] = useState(false);
  const [showTickets, setShowTickets] = useState(false);

  useEffect(() => {
    api.get(`/orders/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => setNf(true));
  }, [orderNumber]);

  if (nf) return <div className="py-24 text-center font-display text-3xl text-white uppercase">Order Not Found</div>;
  if (!order) return <div className="py-20 text-center text-zinc-500">Loading…</div>;

  const validTickets = order.tickets.filter((t) => t.status !== "cancelled");

  const ev = order.event || {};
  const gcalUrl =
    `https://calendar.google.com/calendar/render?action=TEMPLATE` +
    `&text=${encodeURIComponent(ev.name || "ABE Event")}` +
    (ev.start_compact ? `&dates=${ev.start_compact}/${ev.end_compact || ev.start_compact}` : "") +
    `&location=${encodeURIComponent(ev.location || "Huntsville, AL")}` +
    `&details=${encodeURIComponent(`Your ABE ticket order ${order.order_number}. Show your QR code at the door.`)}`;
  const icsUrl = `${API}/orders/${order.order_number}/event.ics`;

  return (
    <div className="mx-auto max-w-xl px-4 sm:px-6 py-10 pb-20">
      <div className="text-center">
        <CheckCircle2 size={52} className="mx-auto text-[#D4AF37]" />
        <h1 className="font-display text-5xl uppercase text-white mt-4 leading-none">You're In.</h1>
        <p className="text-zinc-400 mt-2 text-sm">A confirmation has been sent to your email.</p>
      </div>

      <div className="mt-8 rounded-2xl border border-[#27272A] bg-[#141417] overflow-hidden">
        {order.event?.flyer_image && <img src={imageUrl(order.event.flyer_image)} alt="" className="h-36 w-full object-cover" />}
        <div className="p-5 space-y-2">
          <div className="flex justify-between text-sm"><span className="text-zinc-400">Order Number</span><span className="text-white font-mono">{order.order_number}</span></div>
          <div className="flex justify-between text-sm"><span className="text-zinc-400">Event</span><span className="text-white text-right">{order.event?.name}</span></div>
          <div className="flex justify-between text-sm"><span className="text-zinc-400">Date</span><span className="text-white">{fullDate(order.event?.date)}</span></div>
          {order.venue && <div className="flex justify-between text-sm"><span className="text-zinc-400">Venue</span><span className="text-white text-right">{order.venue.name}</span></div>}
          <div className="flex justify-between text-sm"><span className="text-zinc-400">Tickets</span><span className="text-white">{validTickets.length}</span></div>
          <div className="flex justify-between text-sm pt-1 border-t border-[#27272A] mt-2"><span className="text-zinc-400">Amount Paid</span><span className="text-[#D4AF37] font-bold">{money(order.total)}</span></div>
        </div>
      </div>

      {!showTickets ? (
        <button data-testid="view-tickets-btn" onClick={() => setShowTickets(true)} className="btn-gold w-full mt-6 rounded-lg py-4 font-oswald tracking-widest flex items-center justify-center gap-2">
          <TicketIcon size={18} /> VIEW TICKETS
        </button>
      ) : (
        <div className="mt-6 space-y-4" data-testid="digital-tickets">
          <div className="rounded-xl border border-[#27272A] bg-[#141417] p-4">
            <p className="font-oswald text-[11px] tracking-widest text-[#D4AF37] uppercase mb-3">Save Your Tickets</p>
            <div className="grid grid-cols-2 gap-2">
              <a data-testid="add-apple-wallet" href={icsUrl} className="flex items-center justify-center gap-2 rounded-md bg-black border border-white/20 text-white py-2.5 text-xs font-oswald tracking-wide hover:border-[#D4AF37]">
                <CalendarPlus size={15} /> APPLE WALLET / CALENDAR
              </a>
              <a data-testid="add-google-wallet" href={gcalUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-md bg-black border border-white/20 text-white py-2.5 text-xs font-oswald tracking-wide hover:border-[#D4AF37]">
                <CalendarPlus size={15} /> GOOGLE WALLET / CALENDAR
              </a>
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">Adds the event to your phone's wallet-linked calendar with a reminder. Your scannable QR tickets are below.</p>
          </div>
          {validTickets.map((t) => (
            <div key={t.id} className="rounded-2xl bg-white text-black overflow-hidden shadow-xl">
              <div className="bg-black px-5 py-3 flex items-center justify-between">
                <img src={LOGO_URL} alt="ABE" className="h-6 logo-invert" />
                <span className="font-oswald text-[10px] tracking-widest text-[#D4AF37]">{t.ticket_type_name}</span>
              </div>
              <div className="p-5 flex gap-4 items-center">
                <img src={`${API}/tickets/${t.qr_token}/qr.png`} alt="QR" className="h-28 w-28" />
                <div className="min-w-0">
                  <p className="font-display text-xl uppercase leading-none">{order.event?.name}</p>
                  <p className="text-xs text-zinc-600 mt-1">{fullDate(order.event?.date)}</p>
                  {order.venue && <p className="text-xs text-zinc-600">{order.venue.name}</p>}
                  <p className="text-sm font-semibold mt-2">{t.holder_name}</p>
                  <p className="text-[10px] text-zinc-500 font-mono mt-1">{t.ticket_number}</p>
                  {t.checked_in && <p className="text-[10px] text-green-600 font-bold mt-1">✓ CHECKED IN</p>}
                </div>
              </div>
            </div>
          ))}
          <p className="text-center text-xs text-zinc-500">Show these QR codes at the door for entry. Save or screenshot this page.</p>
        </div>
      )}

      <Link to="/events" className="block text-center mt-8 text-sm text-zinc-400 hover:text-white">← Browse more events</Link>
    </div>
  );
}
